import React, { useCallback } from 'react';

import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import Label from '../Labels/Label';

export type RadioItemData = {
  id: string,
  label?: string,
}

type RadioItemProps = {
  id: string,
  radioGroupId: string,
  selected: boolean,
  disabled: boolean,
  onSelect: (id: string) => void,
  children: React.ReactNode,
}

type RadioGroupProps = {
  id: string,
  selectedItemId: string | undefined,
  className: string | undefined,
  items: RadioItemData[],
  renderItem?: ((item: RadioItemData) => React.ReactElement<any, any> | null) | undefined,
  onSelect: (id: string) => void,
  disabled?: boolean,
  renderAsRow?: boolean,
}

const RadioItem: React.FC<RadioItemProps> = ({
  id,
  selected = false,
  disabled = false,
  children,
  radioGroupId,
  onSelect,
}) => (
  <div className="flex items-start m-1">
    <input
      className="mt-1 mr-2"
      type="radio"
      id={id}
      value={id}
      name={radioGroupId}
      checked={selected}
      onChange={() => onSelect(id)}
      disabled={disabled}
    />
    { children }
  </div>
)

const RadioGroup: React.FC<RadioGroupProps> = ({
  id,
  selectedItemId,
  className,
  renderItem= undefined,
  items = [],
  onSelect = () => {},
  disabled = false,
  renderAsRow = false,
}) => {
  const handleItemSelection = useCallback((selectedId: string) => {
    if (isFunction(onSelect)) {
      onSelect(selectedId);
    }
  }, [onSelect])

  if (!items?.length) {
    return null;
  }
  
  return (
    <div className={classNames(
      'flex',
      { 'flex-col': !renderAsRow },
      { 'justify-center items-center': renderAsRow },
      className,
    )}>
      {
        items.map(item => (
          <RadioItem
            key={`${id}-${item.id}`}
            id={item.id}
            radioGroupId={id}
            onSelect={handleItemSelection}
            selected={selectedItemId === item.id}
            disabled={disabled}
          >
            {
              isFunction(renderItem) ? renderItem(item) : (
                <Label>{item?.label}</Label>
              )
            }
          </RadioItem>
        ))
      }
    </div>
  );
}

export default RadioGroup;
